import {
  Box,
  ChevronDown,
  ChevronUp,
  FileText,
  MapPin,
  MessageCircle,
  Pentagon,
  Search,
  User,
} from "lucide-react";
import { useState } from "react";
import { type BlockSummary, useBlockSummaries } from "../state/graph-hooks";
import { type TypeDef, useTypeRegistry } from "../state/type-registry";
import { usePopover } from "./use-popover";

// Map icon name strings to Lucide components
const ICON_MAP: Record<string, React.FC<{ className?: string }>> = {
  user: User,
  "message-circle": MessageCircle,
  box: Box,
  pentagon: Pentagon,
  "map-pin": MapPin,
  "file-text": FileText,
};

const COLOR_MAP: Record<string, string> = {
  emerald: "text-emerald-400",
  sky: "text-sky-400",
  amber: "text-amber-400",
  stone: "text-stone-400",
  violet: "text-violet-400",
  zinc: "text-zinc-400",
};

const COLOR_BG_MAP: Record<string, string> = {
  emerald: "bg-emerald-500/20",
  sky: "bg-sky-500/20",
  amber: "bg-amber-500/20",
  stone: "bg-stone-500/20",
  violet: "bg-violet-500/20",
  zinc: "bg-zinc-500/20",
};

export function AssetDrawer() {
  const { isOpen, toggle, containerRef } = usePopover();
  const types = useTypeRegistry();
  const blocks = useBlockSummaries();
  const [activeType, setActiveType] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  // Filter out internal blocks (sync source, document root)
  const visibleBlocks = blocks.filter((b) => {
    if (b.id === "__document_root__") return false;
    if (
      !b.type ||
      b.type === "text" ||
      b.type === "heading" ||
      b.type === "document"
    )
      return false;
    return true;
  });

  // Filter by active type
  const typeFiltered = activeType
    ? visibleBlocks.filter((b) => b.type === activeType)
    : visibleBlocks;

  // Filter by search
  const filtered = search
    ? typeFiltered.filter((b) =>
        (b.name ?? "Untitled").toLowerCase().includes(search.toLowerCase()),
      )
    : typeFiltered;

  // Only show types that have spatial = true (canvas-relevant)
  const spatialTypes = types.filter((t) => t.spatial);

  return (
    <div
      ref={containerRef}
      className="absolute bottom-0 left-1/2 -translate-x-1/2 pointer-events-auto z-40 flex flex-col items-center"
    >
      {/* Drawer Body */}
      {isOpen && (
        <div className="w-[520px] max-w-[70vw] rounded-t bg-black/40 backdrop-blur-sm border border-white/10 border-b-0 flex flex-col shadow-2xl">
          {/* Type toggle group */}
          <div className="flex items-center gap-px px-2 py-[2px] border-b border-white/5">
            <button
              className={`flex items-center justify-center w-6 h-5 rounded transition-colors ${
                activeType === null
                  ? "bg-white/15 text-white"
                  : "text-white/40 hover:text-white/70 hover:bg-white/8"
              }`}
              onClick={() => setActiveType(null)}
              title="All"
            >
              <Box className="w-3 h-3" />
            </button>

            <div className="w-px h-3 bg-white/10 mx-0.5" />

            {spatialTypes.map((typeDef) => {
              const Icon = ICON_MAP[typeDef.icon] ?? Box;
              const colorClass = COLOR_MAP[typeDef.color] ?? "text-white/40";
              return (
                <button
                  key={typeDef.id}
                  className={`flex items-center justify-center w-6 h-5 rounded transition-colors ${
                    activeType === typeDef.id
                      ? `bg-white/15 ${colorClass}`
                      : `text-white/40 hover:${colorClass} hover:bg-white/8`
                  }`}
                  onClick={() =>
                    setActiveType(activeType === typeDef.id ? null : typeDef.id)
                  }
                  title={typeDef.label}
                >
                  <Icon className="w-3 h-3" />
                </button>
              );
            })}

            {/* Spacer + search */}
            <div className="flex-1" />
            <div className="flex items-center gap-1">
              <Search className="w-3 h-3 text-white/30" />
              <input
                type="text"
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="bg-transparent text-[11px] text-white/80 outline-none w-20 placeholder:text-white/30"
              />
            </div>
          </div>

          {/* Asset grid */}
          <div className="p-2 overflow-y-auto max-h-36">
            {filtered.length > 0 ? (
              <div className="grid grid-cols-5 gap-1.5">
                {filtered.map((block) => (
                  <AssetCard key={block.id} block={block} types={types} />
                ))}
              </div>
            ) : (
              <div className="text-[10px] text-white/30 text-center py-4">
                {visibleBlocks.length === 0
                  ? "No assets yet. Create blocks to see them here."
                  : "No matches."}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Toggle button */}
      <button
        className={`flex items-center gap-1 rounded-t border border-b-0 border-white/10 px-2 h-5 text-[10px] uppercase font-bold tracking-widest transition-colors ${
          isOpen
            ? "bg-black/40 backdrop-blur-sm text-white/80"
            : "bg-black/40 backdrop-blur-sm text-white/40 hover:text-white/60"
        }`}
        onClick={toggle}
      >
        <Box className="w-3 h-3" />
        Assets
        {isOpen ? (
          <ChevronDown className="w-3 h-3" />
        ) : (
          <ChevronUp className="w-3 h-3" />
        )}
      </button>
    </div>
  );
}

function AssetCard({
  block,
  types,
}: {
  block: BlockSummary;
  types: TypeDef[];
}) {
  const typeDef = types.find((t) => t.id === block.type);
  const bgColor = COLOR_BG_MAP[typeDef?.color ?? "zinc"] ?? "bg-white/5";
  const Icon = ICON_MAP[typeDef?.icon ?? "box"] ?? Box;

  return (
    <div
      className="flex flex-col items-center justify-center p-1.5 rounded border border-white/5 bg-white/5 hover:bg-white/10 hover:border-white/20 cursor-grab active:cursor-grabbing transition-colors"
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData("application/x-vtt-block-id", block.id);
        e.dataTransfer.setData("application/x-vtt-asset-type", block.type);
      }}
    >
      <div
        className={`w-8 h-8 ${bgColor} rounded-full mb-1 border border-white/10 flex items-center justify-center`}
      >
        <Icon className="w-3.5 h-3.5 text-white/50" />
      </div>
      <span className="text-[9px] text-center truncate w-full text-white/50">
        {block.name || "Untitled"}
      </span>
    </div>
  );
}
